import {
  Button,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select as AriaSelect,
  SelectValue,
} from 'react-aria-components';
import styles from './Select.module.css';

/** Eine auswählbare Option. */
export interface SelectOption {
  /** Eindeutiger Schlüssel, der an `onChange` übergeben wird. */
  id: string;
  /** Angezeigter, bereits übersetzter Text. */
  label: string;
}

/** Eigenschaften der Auswahlliste. */
interface SelectProps {
  /** Beschriftung (zugleich Barrierefreiheits-Label). */
  label: string;
  /** Verfügbare Optionen. */
  options: SelectOption[];
  /** Platzhaltertext, solange nichts gewählt ist. */
  placeholder: string;
  /** Schlüssel der aktuellen Auswahl oder `null`. */
  value: string | null;
  /** Wird mit dem Schlüssel der neu gewählten Option aufgerufen. */
  onChange: (id: string) => void;
}

/**
 * Barrierefreie Auswahlliste auf Basis von `react-aria-components`.
 * Ersetzt `react-select`, weil diese Bibliothek keine Styles zur Laufzeit als `<style>`-Tags
 * einfügt und damit mit der strikten CSP (`style-src 'self'`) verträglich ist (PLAN.md T12).
 * @param props Beschriftung, Optionen, Platzhalter und Auswahl.
 */
export function Select({ label, options, placeholder, value, onChange }: SelectProps) {
  return (
    <AriaSelect
      className={styles.select}
      placeholder={placeholder}
      selectedKey={value}
      onSelectionChange={(key) => {
        if (key !== null) onChange(String(key));
      }}
    >
      <Label className={styles.label}>{label}</Label>
      <Button className={styles.trigger}>
        <SelectValue className={styles.value} />
        <span aria-hidden="true" className={styles.arrow}>
          ▾
        </span>
      </Button>
      <Popover className={styles.popover}>
        <ListBox className={styles.listbox}>
          {options.map((option) => (
            <ListBoxItem key={option.id} id={option.id} className={styles.item}>
              {option.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
